import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@gogame/query';
import { useScreenMessage } from '@gogame/screens';
import {
  PENDING,
  FINISHED,
  PROCESS,
  ERROR,
  GEMINI_PRO_MODEL,
} from 'components/AIChatbot/constants';
import usePendingRequest from 'components/AIChatbot/hooks/usePendingRequest';
import { useChatContext } from 'components/AIChatbot/context';

import { submitMessage } from '../queryKeyFactory';
import { useOptimisticUpdateMessages } from '../useOptimisticUpdateMessages';
import { createMessage, getJobStatus } from '../../sources/messages';

export const useCreateMessage = () => {
  const { showErrorMessage } = useScreenMessage();
  const updateMessages = useOptimisticUpdateMessages();

  const { chatId: contextChatId, setChatId } = useChatContext();
  const queryClient = useQueryClient();

  const start = usePendingRequest({
    timeout: 1000,
    source: getJobStatus,
    onError: (error) => {
      showErrorMessage(error.message);
    },
    onUpdate: ({ status, response, error, jobId, chatId }) => {
      switch (status) {
        case FINISHED:
          updateMessages(chatId, response, jobId);
          return false;
        case PENDING:
          return true;
        case PROCESS:
          updateMessages(chatId, response, jobId);
          return true;
        case ERROR:
          showErrorMessage(error);
          return false;
        default:
          return false;
      }
    },
  });

  const handleSuccess = useCallback(
    async (response, params) => {
      if (`${response.chatId}` !== `${contextChatId}`) {
        setChatId(response.chatId);
      }
      updateMessages(response.chatId, response.message);

      return start({ jobId: response.jobId }, params);
    },
    [contextChatId, setChatId, updateMessages, queryClient, start]
  );

  const mutation = useMutation(
    submitMessage(),
    (params) =>
      createMessage({
        ...params,
        model: GEMINI_PRO_MODEL,
        chatId: contextChatId,
      }),
    {
      refresh: false,
      successMessage: null,
      onSuccess: handleSuccess,
    }
  );

  const action = useCallback(
    async ({ query, state }) => {
      try {
        return await mutation.mutateAsync({ query, state });
      } catch (e) {
        showErrorMessage(e.message);
        throw e;
      }
    },
    [mutation.mutateAsync, showErrorMessage]
  );

  return [action, mutation];
};
